import React, { useState } from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Button from '@mui/joy/Button';
import Divider from '@mui/joy/Divider';
import type { AIChatMessage, TicketPriority, TicketCategory } from '../../types/support';
import { useCreateTicket } from '../../hooks/useSupport';

interface Props {
  open: boolean;
  onClose: () => void;
  aiContext?: AIChatMessage[];
  onSuccess?: (ticketId: string) => void;
}

function buildAIContextString(messages: AIChatMessage[]): string {
  return messages
    .filter(m => m.role !== 'assistant' || messages.indexOf(m) !== 0) // skip welcome msg
    .map(m => `${m.role === 'user' ? 'You' : 'Assistant'}: ${m.content}`)
    .join('\n\n');
}

function buildDescriptionFromContext(messages: AIChatMessage[]): string {
  const userMessages = messages.filter(m => m.role === 'user');
  return userMessages.map(m => m.content).join(' ').slice(0, 500);
}

export default function EscalateToTicketDialog({ open, onClose, aiContext, onSuccess }: Props) {
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState(
    aiContext ? buildDescriptionFromContext(aiContext) : ''
  );
  const [priority, setPriority] = useState<TicketPriority>('normal');
  const [category, setCategory] = useState<TicketCategory>('general');

  const createTicket = useCreateTicket();

  // Update description when aiContext changes on open
  React.useEffect(() => {
    if (open && aiContext) {
      setDescription(buildDescriptionFromContext(aiContext));
    }
  }, [open, aiContext]);

  const handleSubmit = async () => {
    if (!subject.trim() || !description.trim()) return;

    try {
      await createTicket.mutateAsync({
        subject: subject.trim(),
        description: description.trim(),
        priority,
        category,
        ai_conversation_context: aiContext ? buildAIContextString(aiContext) : '',
      });
      setSubject('');
      setDescription('');
      setPriority('normal');
      setCategory('general');
      onClose();
      // ticket id passed via onSuccess is not needed here — parent uses the tickets list
      onSuccess?.('created');
    } catch {
      // error handled by caller
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ width: { xs: '95vw', sm: 520 }, maxHeight: '90vh', overflowY: 'auto' }}>
        <ModalClose />
        <Typography level="title-lg">Submit a Support Ticket</Typography>
        <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
          Our team will review your ticket and respond as soon as possible.
        </Typography>
        <Divider />

        <Stack spacing={2}>
          <FormControl required>
            <FormLabel>Subject</FormLabel>
            <Input
              placeholder="Brief summary of your issue"
              value={subject}
              onChange={e => setSubject(e.target.value)}
              autoFocus
            />
          </FormControl>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Category</FormLabel>
              <Select value={category} onChange={(_, v) => v && setCategory(v)}>
                <Option value="general">General</Option>
                <Option value="billing">Billing</Option>
                <Option value="technical">Technical</Option>
                <Option value="feature_request">Feature Request</Option>
                <Option value="bug_report">Bug Report</Option>
              </Select>
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Priority</FormLabel>
              <Select value={priority} onChange={(_, v) => v && setPriority(v)}>
                <Option value="low">Low</Option>
                <Option value="normal">Normal</Option>
                <Option value="high">High</Option>
                <Option value="urgent">Urgent</Option>
              </Select>
            </FormControl>
          </Stack>

          <FormControl required>
            <FormLabel>Description</FormLabel>
            <Textarea
              placeholder="Describe your issue in detail…"
              value={description}
              onChange={e => setDescription(e.target.value)}
              minRows={4}
              maxRows={8}
            />
          </FormControl>

          {aiContext && aiContext.length > 1 && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              Your conversation with the AI assistant will be attached to help our team understand the context.
            </Typography>
          )}

          <Stack direction="row" spacing={1} justifyContent="flex-end">
            <Button variant="outlined" color="neutral" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSubmit}
              loading={createTicket.isPending}
              disabled={!subject.trim() || !description.trim()}
            >
              Submit Ticket
            </Button>
          </Stack>
        </Stack>
      </ModalDialog>
    </Modal>
  );
}
